import reportFrontendIncident from './reportFrontendIncident';
import isIgnorableAblyConnectionError from './isIgnorableAblyConnectionError';

/**
 * Installs global runtime-error handlers ONCE. Imperative (not a React hook) so
 * it needs no change to App.js — call it from index.js before rendering.
 * Captures uncaught window errors, resource-load failures and unhandled promise
 * rejections and forwards them to the watchdog.
 */

let installed = false;

function messageOf(errorLike, fallback) {
	if (!errorLike) return fallback;
	if (typeof errorLike === 'string') return errorLike;
	if (typeof errorLike.message === 'string' && errorLike.message) return errorLike.message;
	try {
		return JSON.stringify(errorLike);
	} catch (_) {
		return String(errorLike);
	}
}

function stackOf(errorLike) {
	return errorLike && typeof errorLike === 'object' && typeof errorLike.stack === 'string'
		? errorLike.stack
		: null;
}

export function isOptionalChatAudioResource(resourceUrl) {
	if (typeof resourceUrl !== 'string') {
		return false;
	}

	try {
		return new URL(resourceUrl, window.location.origin).pathname
			.toLowerCase()
			.includes('/static/media/oat_chat_audio.');
	} catch (_) {
		return false;
	}
}

// A device change can complete just after a call disconnects. In that narrow
// teardown window, WebRTC rejects replaceTrack because the connection is gone.
export function isExpectedVideoCallCloseError(errorLike) {
	return /RTCPeerConnection is closed/i.test(messageOf(errorLike, ''));
}

export default function installGlobalErrorReporting() {
	if (installed || typeof window === 'undefined') {
		return;
	}
	installed = true;

	window.addEventListener(
		'error',
		(event) => {
			const resourceUrl = event?.target?.src || event?.target?.href || null;
			const isResourceError = !!resourceUrl && !event.error;
			if (isResourceError && isOptionalChatAudioResource(resourceUrl)) {
				event.preventDefault();
				return;
			}

			if (!isResourceError && (
				isIgnorableAblyConnectionError(event?.error || event?.message)
				|| isExpectedVideoCallCloseError(event?.error || event?.message)
			)) {
				event.preventDefault();
				return;
			}

			reportFrontendIncident({
				type: isResourceError ? 'resource-load-error' : 'window-error',
				message: messageOf(event.error || event.message, 'Unhandled window error'),
				stack: stackOf(event.error),
				metadata: {
					filename: event.filename || null,
					lineno: event.lineno || null,
					colno: event.colno || null,
					resource_url: resourceUrl,
				},
			});
		},
		true
	);

	window.addEventListener('unhandledrejection', (event) => {
		const reason = event?.reason;
		if (isIgnorableAblyConnectionError(reason) || isExpectedVideoCallCloseError(reason)) {
			event.preventDefault();
			return;
		}

		reportFrontendIncident({
			type: 'unhandled-rejection',
			message: messageOf(reason, 'Unhandled promise rejection'),
			stack: stackOf(reason),
			metadata: { reason_type: reason?.name || typeof reason },
		});
	});
}
